import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, GraduationCap, Receipt, Scale, TrendingDown, TrendingUp } from 'lucide-react';
import { useTresoreriePrevisionnelle } from '@/hooks/queries/useTresoreriePrevisionnelle';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import { cn } from '@/lib/utils';

function fmtDA(n: number): string {
  return (
    Math.round(n)
      .toLocaleString('fr-FR', { maximumFractionDigits: 0 })
      .replace(/[\u00A0\u202F\u2009]/g, ' ') + ' DA'
  );
}

interface KpiBlockProps {
  label: string;
  value: number;
  count?: number;
  icon: JSX.Element;
  tone: 'emerald' | 'rose' | 'orange' | 'indigo';
  href?: string;
}

const TONE_CLASSES: Record<KpiBlockProps['tone'], { bg: string; text: string; icon: string }> = {
  emerald: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    icon: 'text-emerald-600 bg-emerald-100',
  },
  rose: { bg: 'bg-rose-50', text: 'text-rose-700', icon: 'text-rose-600 bg-rose-100' },
  orange: { bg: 'bg-orange-50', text: 'text-orange-700', icon: 'text-orange-600 bg-orange-100' },
  indigo: { bg: 'bg-indigo-50', text: 'text-indigo-700', icon: 'text-indigo-600 bg-indigo-100' },
};

function KpiBlock({ label, value, count, icon, tone, href }: KpiBlockProps): JSX.Element {
  const cls = TONE_CLASSES[tone];
  const inner = (
    <div
      className={cn(
        'flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all',
        href && 'hover:-translate-y-0.5 hover:shadow-md',
      )}
    >
      <div className={cn('rounded-xl p-2.5', cls.icon)}>{icon}</div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
          {label}
        </div>
        <div className={cn('mt-1 break-words text-2xl font-black tabular-nums', cls.text)}>
          {fmtDA(value)}
        </div>
        {count !== undefined && (
          <div className="mt-0.5 text-[11px] text-slate-500">
            {count} ligne{count > 1 ? 's' : ''}
          </div>
        )}
      </div>
    </div>
  );
  return href ? <Link to={href}>{inner}</Link> : inner;
}

export function TresorerieDashboardWidget(): JSX.Element {
  const { data: entreprises = [] } = useEntreprises();
  const [entrepriseId, setEntrepriseId] = useState<string>('');
  const { data, isLoading } = useTresoreriePrevisionnelle(entrepriseId || null);

  const aRecevoir = data?.aRecevoir ?? 0;
  const detteFormateurs = data?.detteFormateurs ?? 0;
  const detteFournisseurs = data?.detteFournisseurs ?? 0;
  const soldeNet = data?.soldeNet ?? 0;
  const positif = soldeNet >= 0;

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-base font-bold text-slate-800">Trésorerie prévisionnelle</h2>
          <p className="text-xs text-slate-500">
            Encaissements en attente vs dettes — solde net prévisionnel
          </p>
        </div>
        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
            Périmètre
          </label>
          <select
            value={entrepriseId}
            onChange={(e) => setEntrepriseId(e.target.value)}
            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
          >
            <option value="">— Toutes les entreprises —</option>
            {entreprises.map((e) => (
              <option key={e.id} value={e.id}>
                {e.nom}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiBlock
          label="À recevoir"
          value={aRecevoir}
          count={data?.nbFacturesEnCours}
          icon={<Receipt className="h-5 w-5" />}
          tone="emerald"
          href="/finances/consolides/recouvrement"
        />
        <KpiBlock
          label="Dette formateurs"
          value={detteFormateurs}
          count={data?.nbPaiementsFormateurs}
          icon={<GraduationCap className="h-5 w-5" />}
          tone="orange"
          href="/finances/consolides/formateurs"
        />
        <KpiBlock
          label="Dette fournisseurs"
          value={detteFournisseurs}
          count={data?.nbDettesFournisseurs}
          icon={<Building2 className="h-5 w-5" />}
          tone="rose"
          href="/finances/consolides/fournisseurs"
        />
        <KpiBlock
          label={positif ? 'Solde net (favorable)' : 'Solde net (déficitaire)'}
          value={soldeNet}
          icon={positif ? <TrendingUp className="h-5 w-5" /> : <TrendingDown className="h-5 w-5" />}
          tone={positif ? 'indigo' : 'rose'}
        />
      </div>

      {/* Récap synthèse */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-xs">
        <Scale className="h-4 w-4 text-slate-500" />
        <span className="text-slate-700">
          <strong>{fmtDA(aRecevoir)}</strong> à encaisser
        </span>
        <span className="text-slate-400">−</span>
        <span className="text-slate-700">
          <strong>{fmtDA(detteFormateurs + detteFournisseurs)}</strong> à payer
          <span className="ml-1 text-[10px] text-slate-500">
            ({fmtDA(detteFormateurs)} formateurs + {fmtDA(detteFournisseurs)} fournisseurs)
          </span>
        </span>
        <span className="text-slate-400">=</span>
        <span
          className={cn('font-bold tabular-nums', positif ? 'text-emerald-700' : 'text-rose-700')}
        >
          {fmtDA(soldeNet)}
        </span>
        {isLoading && <span className="text-[10px] italic text-slate-400">— chargement…</span>}
      </div>
    </section>
  );
}
